import {
  voucherStatusName,
  type TripStatus,
  type VoucherStatusName,
  type VoucherStatusNameInput,
} from '@voll-intelligence/domain';

import type { ToneChipTone } from './tone_chip';

type LabelKey = Parameters<(typeof import('@web/i18n'))['t']>[0];

/** A status badge spec: the tonal dot variant + the i18n key for its label. */
export interface StatusBadgeSpec {
  tone: ToneChipTone;
  labelKey: LabelKey;
}

/** O tom de cada nome: o mesmo de antes para o mesmo estado. */
const NAME_TONE: Record<VoucherStatusName, ToneChipTone> = {
  REQUESTED: 'ai',
  WAITING_FOR_QUOTATION: 'ai',
  WAITING_FOR_EMISSION: 'ai',
  AWAITING_APPROVAL: 'amber',
  CANCELLATION_REQUESTED: 'amber',
  CHANGE_REQUESTED: 'amber',
  EXPIRED: 'amber',
  EMITTED: 'green',
  CANCELED: 'red',
  REPROVED: 'red',
  REQUEST_DENIED: 'red',
};

/**
 * Voucher → tonal dot badge (the `vj-badge` of the handoff), for voucher rows and
 * the drawer hero. The label is the legacy VOLL name read from the raw status
 * (`voucherStatusName`), as the shared `trips.voucherStatus.*` key the trip-document
 * catalog brings into our i18next. Null when the voucher has no status.
 */
export function voucherStatusBadge(voucher: VoucherStatusNameInput): StatusBadgeSpec | null {
  const name = voucherStatusName(voucher);
  return name ? { tone: NAME_TONE[name], labelKey: `trips.voucherStatus.${name}` } : null;
}

/**
 * Trip status → tonal dot badge. `UPCOMING` is the brand "Próxima" pill (vs. the
 * old gray), matching the handoff's status→colour map. Exhaustive over `TripStatus`.
 */
export function tripStatusBadge(status: TripStatus): StatusBadgeSpec {
  switch (status) {
    case 'UPCOMING':
      return { tone: 'brand', labelKey: 'customers.status.upcoming' };
    case 'ONGOING':
      return { tone: 'ai', labelKey: 'customers.status.inProgress' };
    case 'COMPLETED':
      return { tone: 'gray', labelKey: 'customers.status.completed' };
    case 'CANCELLED':
      return { tone: 'red', labelKey: 'customers.status.cancelled' };
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}
